<template>
  <div class="form-group">
    <label :for="name">
      {{
        props.label
          ? t(`labels.${props.label}`, props.label)
          : t(`labels.${props.name}`, props.name)
      }}
    </label>
    <Select
      :id="name"
      :key="locale"
      v-model="internalValue"
      :class="['w-full', { 'max-w-[260px] truncate': props.compact }]"
      :options="processedOptions"
      :filter="props.filter ?? false"
      optionLabel="name"
      :data-testid="props.testId"
      :showClear="props.showClear ?? false"
      :placeholder="
        props.placeholder
          ? t(`placeholder.${props.placeholder}`, props.placeholder)
          : props.placeholder || '&nbsp'
      "
      :disabled="disabled"
      @change="handleChange"
    >
      <template #value="slotProps">
        <div v-if="slotProps.value" class="flex align-items-center">
          <div
            v-tooltip="
              props.compact
                ? {
                    value: slotProps.value.name,
                    fitContent: true,
                    showDelay: 300,
                  }
                : null
            "
            :class="props.compact ? 'truncate' : ''"
            :style="props.compact ? 'max-width: 220px;' : ''"
          >
            {{ slotProps.value.name }}
          </div>
        </div>
        <span v-else>
          {{ slotProps.placeholder }}
        </span>
      </template>

      <template #option="slotProps">
        <div
          v-tooltip="
            props.compact
              ? {
                  value: slotProps.option.name,
                  fitContent: true,
                  showDelay: 300,
                }
              : null
          "
          :class="[{ truncate: props.compact }, 'flex align-items-center']"
          :style="props.compact ? 'max-width: 240px;' : ''"
        >
          {{ slotProps.option.name }}
        </div>
      </template>
    </Select>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import type { SelectChangeEvent } from 'primevue/select';
import { useI18n } from 'vue-i18n';
type SelectOption = { id: string | number; name: string };

const props = defineProps<{
  name: string;
  placeholder?: string;
  options: Array<string | SelectOption>;
  modelValue: string | number | undefined;
  label?: string;
  disabled?: boolean;
  filter?: boolean;
  showClear?: boolean;
  untranslated?: boolean;
  compact?: boolean;
  initialValue?: string | number | undefined; // unused prop, but given for easy switch from FormSelect
  isDirty?: (dirty: boolean) => void; // unused prop, but given for easy switch from FormSelect
  testId?: string;
}>();

const { t, locale } = useI18n();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | undefined): void;
  (e: 'change', event: SelectChangeEvent): void;
}>();

const internalValue = ref<SelectOption | null>(null);

// Type Guard
function isSelectOption(option: string | SelectOption): option is SelectOption {
  return typeof option === 'object' && option != null && 'id' in option && 'name' in option;
}

const processedOptions = computed<SelectOption[]>(() =>
  props.options.map((option) => {
    if (!isSelectOption(option)) {
      return {
        id: option,
        name: props?.untranslated ? option : t(`enums.${option?.toUpperCase()}`, option),
      };
    }
    return {
      ...option,
      name: props?.untranslated
        ? option.name
        : t(`enums.${option.name?.toUpperCase()}`, option.name),
    };
  }),
);

watch(locale, () => {
  if (internalValue.value) {
    internalValue.value.name = props?.untranslated
      ? internalValue.value.name
      : t(`enums.${internalValue.value.id}`, internalValue.value.name);
  }
});

watch(internalValue, (newValue) => {
  emit('update:modelValue', newValue ? newValue.id : undefined);
});

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value =
      processedOptions.value.find((obj) => String(obj.id) === String(newValue)) ?? null;
  },
);

watch(
  () => props.options,
  () => {
    internalValue.value =
      processedOptions.value.find((obj) => String(obj.id) === String(props.modelValue)) ?? null;
  },
  { immediate: true },
);

onMounted(() => {
  internalValue.value =
    processedOptions.value.find((obj) => String(obj.id) === String(props.modelValue)) ?? null;
});

function handleChange(event: SelectChangeEvent) {
  emit('change', event);
}
</script>

<style scoped lang="css">
/* :deep() */
p-disabled .p-select-dropdown-icon {
  display: none;
}
/* :deep() */
.p-select {
  line-height: normal;
  font-size: 16px;
}
</style>
